<template>
  <div>
    <a class="pointer"
       @click="startSettings">
      <CogSvg class="svg-stroke-gray"/>
    </a>
    <b-modal id="data-table-column-settings-modal"
             v-if="showSettingsModal"
             size="md"
             :title="$t('table_settings')"
             @hide="hide"
             @hidden="showSettingsModal = false"
             centered>

      <div class="">
        <draggable
            v-model="columns"
            item-key="id"
            handle=".drag-handle"
            class="field-list"
            @end="onDragEnd"
        >
          <template #item="{ element }">
            <div class="field-row"
                 :class="{
                   'hidden': hiddenColumns.includes(element.id),
                   'animating': animatingColumn === element.id
                 }"
                 :key="`column-${element.id}`">
              <div class="row no-gutters align-items-center">
                <div class="col-auto">
                  <a class="px-2 cursor-move drag-handle">
                    <DragSvg class="svg-gray svg-size-md"/>
                  </a>
                </div>
                <div class="col">
                  {{ $t(element.title) }}
                </div>
                <div class="col-auto">
                  <a class="p-2 pointer pin-toggler"
                     :class="{ pinned: pinPointColumns.includes(element.id) }"
                     @click.stop="pinPointColumn(element.id)">
                    <PinnedSvg v-if="pinPointColumns.includes(element.id)"
                               class="svg-size-md"/>
                    <NotPinnedSvg v-else
                                  class="svg-size-md"/>
                  </a>
                </div>
                <div class="col-auto">
                  <a class="p-2 pointer eye-toggler"
                     :class="{ hidden: hiddenColumns.includes(element.id) }"
                     @click.stop="toggleColumn(element.id)">
                    <EyeClosedSvg class="svg-size-md"
                                  v-if="hiddenColumns.includes(element.id)"/>
                    <EyeSvg v-else
                            class="svg-size-md"/>
                  </a>
                </div>
              </div>
            </div>
          </template>
        </draggable>
      </div>

      <template #modal-footer>
        <div class="modal-footer-buttons">
          <b-button class="gray"
                    variant="themed"
                    @click="reset">
            {{ $t('reset_verb') }}
          </b-button>
          <b-button class="ml-2 w-auto px-3"
                    variant="themed"
                    @click="save">
            {{ $t('save_settings') }}
          </b-button>
        </div>
      </template>

    </b-modal>
  </div>
</template>

<script setup>
import CogSvg from "@/assets/svg-vue/general/cog.svg.vue"
import NotPinnedSvg from "@/assets/svg-vue/general/pin-empty.svg.vue"
import PinnedSvg from "@/assets/svg-vue/general/pin-bold.svg.vue"
import EyeSvg from '@/assets/svg-vue/form/eye_filled.svg'
import EyeClosedSvg from '@/assets/svg-vue/form/closed_eye.svg'
import DragSvg from '@/assets/svg-vue/general/drag.svg'
import { computed, getCurrentInstance, nextTick, onBeforeMount, ref } from "vue"
import { useStore } from "vuex"
import {useRoute} from "vue-router"
import draggable from "vuedraggable"
import {copyObject} from "@/extensions/prototypes/prototypes"
import { orderDataTableColumns } from "@/services/dataTable.service"

draggable.compatConfig = { MODE: 3 }

const props = defineProps({
  options: {
    type: Object
  },
})

const store = useStore()
const route = useRoute()

const emit = defineEmits(['refreshColumnSettings'])

let bvModal = null
// let noty = null
// let i18n = null

const showSettingsModal = ref(false)
const hiddenColumns = ref([])
const pinPointColumns = ref([])
const orderColumns = ref([])
const columns = ref([])
const animatingColumn = ref(null)

const rights = computed(() => store.state.auth.rights || [])

onBeforeMount(() => {
  const instance = (getCurrentInstance())
  bvModal = instance?.ctx?._bv__modal
  // noty = instance?.appContext?.app?.noty
  // i18n = instance?.ctx?.$i18n
})

const startSettings = async () => {
  showSettingsModal.value = true
  hiddenColumns.value = JSON.parse(window.localStorage.getItem(route.name+'_hiddencolumns') || "[]")
  pinPointColumns.value = JSON.parse(window.localStorage.getItem(route.name+'_pinpointcolumns') || "[]")
  orderColumns.value = JSON.parse(window.localStorage.getItem(route.name+'_ordercolumns') || "[]")
  columns.value = orderDataTableColumns(copyObject(props.options.fields.filter(field => showTdInTable(field))), orderColumns.value)
  await nextTick()
  bvModal.show('data-table-column-settings-modal')
}

const showTdInTable = (field) => {
  if(!field.table) return false
  if(field.table instanceof Function) {
    if(!field.table(store)) {
      return false
    }
  }
  if(field.right_excludes && rights.value.includes(field.right_excludes)) {
    return false
  }
  return !(field.right_includes && !rights.value.includes(field.right_includes))
}

const save = async () => {
  window.localStorage.setItem(route.name+'_hiddencolumns', JSON.stringify(hiddenColumns.value))
  window.localStorage.setItem(route.name+'_pinpointcolumns', JSON.stringify(pinPointColumns.value))
  window.localStorage.setItem(route.name+'_ordercolumns', JSON.stringify(columns.value.map(field => field.id)))
  close()
}

const reset = async () => {
  window.localStorage.setItem(route.name+'_hiddencolumns', JSON.stringify([]))
  window.localStorage.setItem(route.name+'_pinpointcolumns', JSON.stringify([]))
  window.localStorage.setItem(route.name+'_ordercolumns', JSON.stringify([]))
  close()
}

const close = () => {
  bvModal.hide('data-table-column-settings-modal')
}

const hide = () => {
  emit('refreshColumnSettings')
}

const toggleColumn = (id) => {
  const index = hiddenColumns.value.indexOf(id)
  const columnIndex = columns.value.findIndex(col => col.id === id)
  const column = columns.value[columnIndex]

  if (index > -1) {
    // Unhiding column - remove from hidden list and move to end of visible columns
    hiddenColumns.value.splice(index, 1)

    // Remove column from current position
    columns.value.splice(columnIndex, 1)

    // Find the last visible column position
    let lastVisibleIndex = -1
    for (let i = columns.value.length - 1; i >= 0; i--) {
      if (!hiddenColumns.value.includes(columns.value[i].id)) {
        lastVisibleIndex = i
        break
      }
    }

    // Insert after the last visible column (or at the beginning if no visible columns)
    columns.value.splice(lastVisibleIndex + 1, 0, column)
  } else {
    // Hiding column - add to hidden list and move to end of list
    hiddenColumns.value.push(id)

    // Remove column from current position
    columns.value.splice(columnIndex, 1)

    columns.value.push(column)
  }
}

const animateRow = (id) => {
  animatingColumn.value = id
  setTimeout(() => {
    animatingColumn.value = null
  }, 400)
}

const pinPointColumn = (id) => {
  const index = pinPointColumns.value.indexOf(id)
  if (index > -1) {
    pinPointColumns.value.splice(index, 1)
  } else {
    pinPointColumns.value.push(id)
  }
  animateRow(id)
}

const onDragEnd = (evt) => {
  if (evt.newIndex !== evt.oldIndex) {
    const movedColumn = columns.value[evt.newIndex]
    animateRow(movedColumn.id)
  }
}
</script>

<style scoped lang="scss">
.field-row {
  margin: 8px 0;
  background: $body-bg;
  padding: 6px;
  border-radius: 6px;
  border: 1px solid transparent;
  transition: all 0.4s ease-in-out;
  &.hidden {
    opacity: 0.4;
  }
  &.sortable-chosen {
    border: 1px solid $label-color;
  }
  &.animating {
    background: #E3EFFA !important;
  }
}
.pin-toggler {
  svg {
    fill: $label-color;
  }
  &:hover {
    svg {
      fill: $gray-text;
    }
  }
  &.pinned {
    svg {
      fill: black;
    }
    &:hover {
      svg {
        fill: $gray-text;
      }
    }
  }
}
.eye-toggler {
  svg {
    fill: black;
  }
  &:hover {
    svg {
      fill: $gray-text;
    }
  }
  &.hidden {
    svg {
      fill: $gray-text;
    }
    &:hover {
      svg {
        fill: black;
      }
    }
  }

}
</style>
